import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, startAfter, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ShadowCard } from '../components/Feed/ShadowCard';
import { NativeAdCard } from '../components/Feed/NativeAdCard';
import { getAnonymousName, getAvatarForShadow } from '../utils/shadowIdentity';
import { useNavigationCache } from '../context/NavigationCacheContext';
import sparkLogo from '../assets/spark-logo.png';
import { Globe, Heart, Flame, Zap, Smile, Frown, Ghost, Moon, Megaphone, CloudRain, Star, MessageCircle, Flower2, AlertTriangle, Sparkles, BarChart2, Bell } from 'lucide-react';

const tags = [
  { name: 'Love',       icon: Heart,         color: '#FF6F61' },
  { name: 'Angry',      icon: Flame,         color: '#EF4444' },
  { name: 'Roast',      icon: Zap,           color: '#F97316' },
  { name: 'Crush',      icon: Heart,         color: '#F43F5E' },
  { name: 'Funny',      icon: Smile,         color: '#34D399' },
  { name: 'Regret',     icon: Frown,         color: '#6B7280' },
  { name: 'Secret',     icon: Ghost,         color: '#EC4899' },
  { name: 'Dream',      icon: Moon,          color: '#3B82F6' },
  { name: 'Vent',       icon: Megaphone,     color: '#DC2626' },
  { name: 'Sad',        icon: CloudRain,     color: '#6366F1' },
  { name: 'Hope',       icon: Star,          color: '#22C55E' },
  { name: 'Confession', icon: MessageCircle, color: '#8B5CF6' },
  { name: 'Gratitude',  icon: Flower2,       color: '#F472B6' },
  { name: 'Fear',       icon: AlertTriangle, color: '#9CA3AF' },
  { name: 'Joy',        icon: Sparkles,      color: '#E11D48' },
];

const filterTags = [{ name: 'All', icon: Globe, color: '#FFF' }, ...tags];

const globalShadowCache = {
  campus: { shadows: [], lastDoc: null, hasMore: true, isInitialized: false },
  global: { shadows: [], lastDoc: null, hasMore: true, isInitialized: false },
  scrolls: { campus: 0, global: 0 },
  activeTab: 'global',
  activeFilter: ['All'],
};

export const ShadowFeed = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(globalShadowCache.activeTab);
  const [activeFilter, setActiveFilter] = useState(globalShadowCache.activeFilter);
  
  const [campusShadows, setCampusShadows] = useState(globalShadowCache.campus.shadows);
  const [globalShadows, setGlobalShadows] = useState(globalShadowCache.global.shadows);
  
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [likedConf, setLikedConf] = useState(new Set());
  const [userCollege, setUserCollege] = useState('');
  const [userIsPremium, setUserIsPremium] = useState(false);

  const currentUser = auth.currentUser;
  const BATCH_SIZE = 10;
  const filterScrollRef = useRef(null);

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScrollDirection, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollDirection);
  }, [lastScrollY]);

  useEffect(() => {
    const el = filterScrollRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  // Fetch user college
  useEffect(() => {
    if (currentUser) {
      getDoc(doc(db, 'users', currentUser.uid)).then(docSnap => {
        if (docSnap.exists()) {
          setUserCollege(docSnap.data().college || '');
          setUserIsPremium(docSnap.data().isPremium || false);
        }
      });
    }
  }, [currentUser]);

  // Fetch Liked Confessions
  useEffect(() => {
    if (currentUser) {
      const fetchLikes = async () => {
        const likedQuery = query(collection(db, 'users', currentUser.uid, 'likedConf'));
        const likedSnap = await getDocs(likedQuery);
        const likes = new Set(likedSnap.docs.map(d => d.id));
        setLikedConf(likes);
      };
      fetchLikes();
    }
  }, [currentUser]);

  const fetchShadows = useCallback(async (tabToFetch, filterToFetch, isLoadMore = false) => {
    if (!currentUser) return;
    if (tabToFetch === 'campus' && !userCollege) {
      setLoading(false);
      return;
    }

    const tabCache = globalShadowCache[tabToFetch];
    
    // If not load more and we already have initialized cache for this tab, skip fetching
    if (!isLoadMore && tabCache.isInitialized) {
      if (tabToFetch === 'campus') setCampusShadows(tabCache.shadows);
      else setGlobalShadows(tabCache.shadows);
      return;
    }

    try {
      if (!isLoadMore) setLoading(true);
      else setLoadingMore(true);

      const cleanTags = filterToFetch.filter(tag => tag !== 'All').map(tag => tag.toLowerCase().trim());
      let constraints = [];
      
      if (tabToFetch === 'campus') {
        constraints.push(where('college', '==', userCollege));
        constraints.push(where('visibility', '==', 'campus'));
      } else {
        constraints.push(where('visibility', '==', 'global'));
      }
      
      if (cleanTags.length === 1) {
        constraints.push(where('tags', 'array-contains', cleanTags[0]));
      } else if (cleanTags.length > 1) {
        constraints.push(where('tags', 'array-contains-any', cleanTags));
      }
      
      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(BATCH_SIZE));

      if (isLoadMore && tabCache.lastDoc) {
        constraints.push(startAfter(tabCache.lastDoc));
      }

      const q = query(collection(db, 'confessions'), ...constraints);
      const snapshot = await getDocs(q);
      
      const newShadows = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      const finalShadows = isLoadMore ? [...tabCache.shadows, ...newShadows] : newShadows;
      
      tabCache.shadows = finalShadows;
      tabCache.lastDoc = snapshot.docs[snapshot.docs.length - 1];
      tabCache.hasMore = snapshot.docs.length === BATCH_SIZE;
      tabCache.isInitialized = true;

      if (tabToFetch === 'campus') {
        setCampusShadows(finalShadows);
      } else {
        setGlobalShadows(finalShadows);
      }

    } catch (err) {
      console.error("Error fetching shadows:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userCollege, currentUser]);

  // Initial fetch and Tab/Filter changes
  useEffect(() => {
    globalShadowCache.activeTab = activeTab;
    globalShadowCache.activeFilter = activeFilter;
    if (currentUser && (activeTab !== 'campus' || userCollege)) {
      fetchShadows(activeTab, activeFilter, false);
    }
  }, [activeTab, activeFilter, currentUser, userCollege, fetchShadows]);

  // Handle activeTab change via UI (Filter changes must reset cache!)
  const handleFilterChange = (newFilter) => {
    // Reset caches for both tabs when filter changes
    globalShadowCache.campus = { shadows: [], lastDoc: null, hasMore: true, isInitialized: false };
    globalShadowCache.global = { shadows: [], lastDoc: null, hasMore: true, isInitialized: false };
    setCampusShadows([]);
    setGlobalShadows([]);
    globalShadowCache.scrolls.campus = 0;
    globalShadowCache.scrolls.global = 0;
    setActiveFilter(newFilter);
  };

  // Restore scroll position flawlessly on mount and tab switch
  React.useLayoutEffect(() => {
    window.scrollTo(0, globalShadowCache.scrolls[activeTab] || 0);
  }, [activeTab]);

  // Infinite scroll and continuous scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      globalShadowCache.scrolls[activeTab] = window.scrollY;
      
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      // Fetch next batch when user has scrolled through 35% of the currently available scrollable area
      if (maxScroll > 0 && window.scrollY >= maxScroll * 0.35) {
        if (!loadingMore && globalShadowCache[activeTab].hasMore) {
          fetchShadows(activeTab, activeFilter, true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchShadows, loadingMore, activeTab, activeFilter]);

  const handleLikeUpdate = (shadowId, isLiked) => {
    setLikedConf(prev => {
      const newSet = new Set(prev);
      if (isLiked) newSet.add(shadowId);
      else newSet.delete(shadowId);
      return newSet;
    });
  };

  const handleDelete = (shadowId) => {
    if (activeTab === 'campus') {
      setCampusShadows(prev => prev.filter(s => s.id !== shadowId));
      globalShadowCache.campus.shadows = globalShadowCache.campus.shadows.filter(s => s.id !== shadowId);
    } else {
      setGlobalShadows(prev => prev.filter(s => s.id !== shadowId));
      globalShadowCache.global.shadows = globalShadowCache.global.shadows.filter(s => s.id !== shadowId);
    }
  };

  const currentShadows = activeTab === 'campus' ? campusShadows : globalShadows;

  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;
    setActiveTab(newTab);
  };

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col pb-20 sm:pb-0 shrink-0">
      {/* Sticky Header */}
      <div className={`sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex flex-col pt-2 transition-transform duration-300 ${!isHeaderVisible ? '-translate-y-full' : 'translate-y-0'}`}>
         <div className="flex items-center justify-between px-4 sm:hidden pb-2">
            <img src={sparkLogo} alt="Spark" className="h-9 w-auto object-contain" />
            <button onClick={() => navigate('/notifications')} className="p-2 relative hover:bg-white/10 rounded-full transition-colors">
               <Bell size={22} className="text-gray-300" />
            </button>
         </div>
         <div className="flex w-full h-14 border-b border-gray-800/50 items-center">
            <button onClick={() => navigate('/shadow-leaderboard')} className="p-2 ml-4 hover:bg-[#1a1a1a] rounded-full transition-colors text-white border border-[#2a2a2a] bg-[#111]">
              <BarChart2 size={18} />
            </button>
            <button 
              onClick={() => handleTabChange('campus')}
              className={`flex-1 flex justify-center items-center relative transition-colors ${activeTab === 'campus' ? 'text-white font-bold' : 'text-gray-500 font-medium'}`}
            >
              Campus
              {activeTab === 'campus' && (
                <div className="absolute bottom-0 w-16 h-1 bg-[#0BD9E5] rounded-t-full" />
              )}
            </button>
            <button 
              onClick={() => handleTabChange('global')}
              className={`flex-1 flex justify-center items-center relative transition-colors ${activeTab === 'global' ? 'text-white font-bold' : 'text-gray-500 font-medium'}`}
            >
              Global
              {activeTab === 'global' && (
                <div className="absolute bottom-0 w-16 h-1 bg-[#0BD9E5] rounded-t-full" />
              )}
            </button>
         </div>
         {/* Filters Row */}
         <div ref={filterScrollRef} className="w-full overflow-x-auto py-3 px-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex gap-2 min-w-max">
               {filterTags.map((tag) => {
                 const isSelected = activeFilter.includes(tag.name);
                 const Icon = tag.icon;
                 return (
                   <button
                     key={tag.name}
                     onClick={() => {
                        const cleanTag = tag.name;
                        if (cleanTag === 'All') {
                          handleFilterChange(['All']);
                        } else {
                          const prev = activeFilter.filter(t => t !== 'All');
                          if (prev.includes(cleanTag)) {
                            const newFilters = prev.filter(t => t !== cleanTag);
                            handleFilterChange(newFilters.length === 0 ? ['All'] : newFilters);
                          } else {
                            handleFilterChange([...prev, cleanTag]);
                          }
                        }
                     }}
                     className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${isSelected ? 'bg-gray-800 border-gray-600' : 'bg-transparent border-gray-800 hover:border-gray-700'}`}
                   >
                     <Icon size={14} color={tag.color} />
                     <span className={`text-[13px] font-medium ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                        {tag.name}
                     </span>
                   </button>
                 );
               })}
            </div>
            {/* Empty State */}
            {!loading && !loadingMore && currentShadows.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mb-4">
                  <Ghost size={40} className="text-gray-600" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No Shadows Found</h3>
                <p className="text-gray-500 text-[15px] max-w-[250px]">
                  {activeFilter.length > 0 && !activeFilter.includes('All') 
                    ? `No shadows found for this tag.` 
                    : activeTab === 'campus' 
                    ? `No shadows found for your college. Be the first to share!` 
                    : `No global shadows yet. The world is waiting!`}
                </p>
              </div>
            )}
         </div>
      </div>

      <div className="">
        {loading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#0BD9E5]"></div>
          </div>
        ) : activeTab === 'campus' && !userCollege ? (
          <div className="text-center p-8 text-gray-500">
            Please add a college to your profile to view campus shadows.
          </div>
        ) : currentShadows.length === 0 ? null : (
          currentShadows.map((shadow, index) => (
            <React.Fragment key={shadow.id}>
              <ShadowCard 
                shadow={shadow} 
                isLiked={likedConf.has(shadow.id)}
                onLikeUpdate={handleLikeUpdate}
                onDelete={handleDelete}
              />
              {!userIsPremium && (index + 1) % 10 === 0 && <NativeAdCard />}
            </React.Fragment>
          ))
        )}
        
        {loadingMore && (
          <div className="flex justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#0BD9E5]"></div>
          </div>
        )}
      </div>
    </main>
  );
};
